// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The pinch the bench reads (the 1.0.106 headset round, 27 Sep 2026: "it takes many tries to get
// a component to release, and in the proper location").
//
// The log said why, twice over:
// - **The release asked for 40 mm.** The shared pinch opens only when thumb and index are 40 mm
//   apart (`PINCH_OPEN`, chosen so a held plunger never flickers). An author holding a part at
//   9 mm who relaxes the fingers to 25–38 mm is still "pinched"; five carries lasted 2.4–9.5 s.
// - **The release moved the part.** The pinch point is the middle of thumb and index, and it
//   slides as the fingers open, so the frame that finally let go put the part where the opening
//   fingers had dragged it.
//
// So the bench has its own pinch, read from the same gap:
// - It closes at `PINCH_CLOSE` once the fingers have come `REARM_GAP` in from their widest since
//   the last release. A hand that has not opened since it appeared does not take; a curled hand
//   that never opens to 40 mm still can.
// - It opens `RELEASE_GAP` wider than the tightest it was held, and never under `PINCH_CLOSE`.
// - It is **settling** once the fingers have opened `SETTLE_GAP` past their tightest — and on
//   the frame it opens. The host keeps the pinch point still while settling, so the part lands
//   where it was when the fingers began to let go.
// - It lets go once the fingers have read open on `RELEASE_FRAMES` of the last `RELEASE_WINDOW`
//   frames (1.0.245; three in a row in 1.0.244), so a one-frame flicker of the tracked gap does not
//   end a held pull or carry, and an opening whose gap jitters still lets go when it should.

import { PINCH_CLOSE } from '../xrInput'

/** How far past the tightest hold the fingers open to let go, metres. */
const RELEASE_GAP = 0.012
/**
 * The narrowest a held pinch lets go at, metres, whatever it closed at (1.0.242): 1.0.235's 15 mm
 * close line took the release floor with it, and a part held at 8 mm let go at 20 instead of 25 —
 * three magnets in a row on the owner's 26 Oct log dropped 40-50 ms after they were taken.
 */
const RELEASE_FLOOR = 0.025
/** How far in from the widest since the release the fingers close to take again, metres. */
const REARM_GAP = 0.012
/** How far past the tightest hold counts as letting go already, for the still pinch point, metres. */
const SETTLE_GAP = 0.004
/**
 * How many of the last `RELEASE_WINDOW` frames the fingers read open before the pinch lets go
 * (1.0.244): about 40 ms at 72 Hz. On the owner's 26 Oct 07:00 log a pull read open for one frame at
 * 19 mm and closed again 14 ms later: the plunger fired a 0.35 launch of the waiting ball, and the
 * full pull that followed met an empty lane. While an open frame is in the window the pinch is settling.
 */
export const RELEASE_FRAMES = 3
/**
 * The frames `RELEASE_FRAMES` are counted over (1.0.245). Three in a row (1.0.244) started again
 * whenever a fast-opening hand's gap jittered under the line, and the frames between drew the rod
 * back in with a hand moving forward: on the owner's 26 Oct 08:01 log the rod stood at 55 mm and
 * 150 ms later let go "at 0 mm".
 */
export const RELEASE_WINDOW = 4

export class PinchCarry {
  /**
   * Where this pinch closes, and the narrowest it lets go at: the shared line for the bench; the
   * plunger rod's is wider (1.0.241), because fingers round a knob do not meet — see
   * `ROD_PINCH_CLOSE` in `cabinetRig.ts`.
   */
  constructor(
    private readonly close: number = PINCH_CLOSE,
    /** Settle only near the release (the rod, 1.0.243), rather than just past the tightest hold. */
    private readonly settleNearRelease = false,
  ) {}

  private closedNow = false
  private settlingNow = false
  private least = 0
  private peak = 0
  /** The last RELEASE_WINDOW held frames, newest in the lowest bit: 1 where the fingers read open. */
  private openBits = 0

  get closed(): boolean {
    return this.closedNow
  }

  get settling(): boolean {
    return this.settlingNow
  }

  /** One frame of one hand: whether its gap was measured, and the thumb-to-index gap in metres. */
  advance(measured: boolean, gap: number): void {
    const was = this.closedNow
    const letGoBefore = this.letGoAt()
    if (!measured) {
      this.reset()
      return
    }
    if (this.closedNow) {
      this.least = Math.min(this.least, gap)
      this.openBits = ((this.openBits << 1) | (gap >= this.letGoAt() ? 1 : 0)) & ((1 << RELEASE_WINDOW) - 1)
      if (countBits(this.openBits) >= RELEASE_FRAMES) {
        this.closedNow = false
        this.peak = gap
        this.openBits = 0
      }
    } else {
      this.peak = Math.max(this.peak, gap)
      if (gap <= this.close && gap <= this.peak - REARM_GAP) {
        this.closedNow = true
        this.least = gap
        this.openBits = 0
      }
    }
    // The rod settles within the last few millimetres before letting go (1.0.243), not SETTLE_GAP
    // past the tightest hold: a pinch held at 8 mm settled from 12 mm on, 13 mm before its 25 mm
    // release, and the rod's draw froze for the whole pull — three of twelve takes on the owner's
    // 26 Oct 05:53 log. The bench keeps its rule: there the still point lands a part where it was shown.
    // An open frame still in the window keeps it settling (1.0.245), so a frame whose gap jitters
    // back under the line does not move the rod or the still point.
    const opening = this.closedNow && this.openBits !== 0
    this.settlingNow = this.settleNearRelease
      ? was && (!this.closedNow || opening || gap > Math.min(letGoBefore, this.letGoAt()) - SETTLE_GAP)
      : was && (opening || gap > this.least + SETTLE_GAP)
  }

  /** The gap this pinch lets go at, held: the close line, the floor, or the hold plus RELEASE_GAP. */
  private letGoAt(): number {
    return Math.max(this.close, RELEASE_FLOOR, this.least + RELEASE_GAP)
  }

  /**
   * Closed at this gap by the caller's own decision — a rod taken by the shared pinch this pinch
   * had not yet re-armed for — so it lets go by this pinch's rule from here.
   */
  hold(gap: number): void {
    this.closedNow = true
    this.settlingNow = false
    this.least = gap
    this.openBits = 0
  }

  /** Nothing measured, the hand gone, the mode left: open, and a fresh hand must open before it takes. */
  reset(): void {
    this.closedNow = false
    this.settlingNow = false
    this.peak = 0
    this.openBits = 0
  }
}

function countBits(bits: number): number {
  let n = 0
  for (; bits !== 0; bits &= bits - 1) n++
  return n
}
